import type { DraftRecording } from '@/types'
import { normalizeTaxonomySelection } from '../taxonomySelection'
import { toUtcIsoString } from '../time'
import { getAbsoluteEndpoint, getUploadsPath, getSubmissionsPath } from '../apiRouting'
import {
  ApiError,
  SubmitResponse,
  blobToBase64,
  buildProtectedAuthHeaders,
  fetchWithTimeout,
  readApiError,
  throwIfAuthRejected,
  throwIfSubmissionRejected,
} from './client'

export interface PresignUploadResponse {
  submissionId: string
  objectKey: string
  presignedUrl: string
  expiresAt: string
}

export interface FinalizeUploadResponse extends SubmitResponse {
  uploadStatus: 'uploaded' | 'failed'
  processingStatus: 'queued' | 'processing' | 'stitching' | 'completed' | 'partial' | 'failed'
}

function getUploadsEndpoint(pathSuffix: string): string {
  const uploadsBase = getAbsoluteEndpoint('uploadsWrite', getUploadsPath)
  return `${uploadsBase}${pathSuffix}`
}

export async function submitDraftRecording(
  draft: DraftRecording,
  mimeType: string = 'audio/webm',
  chunkMetadata?: {
    sessionId?: string
    chunkIndex?: number
    totalChunks?: number
    isLastChunk?: boolean
    chunkId?: string
  },
): Promise<SubmitResponse> {
  const taxonomySelection = normalizeTaxonomySelection(draft)
  const audioData = await blobToBase64(draft.blob)

  const headers = await buildProtectedAuthHeaders('upload drafts', {
    'Content-Type': 'application/json',
    'Idempotency-Key': draft.id,
  })
  const submissionsUrl = getAbsoluteEndpoint('submissionsWrite', getSubmissionsPath)

  const response = await fetchWithTimeout(submissionsUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      id: draft.id,
      type: 'audio',
      userId: draft.userId,
      assetId: taxonomySelection.assetId,
      indicationId: taxonomySelection.indicationId,
      kiqIds: taxonomySelection.kiqIds,
      insightTags: draft.insightTags ?? [],
      createdAt: toUtcIsoString(draft.createdAt),
      audioData,
      audioMimeType: mimeType,
      durationMs: draft.durationMs,
      chunkSessionId: chunkMetadata?.sessionId,
      chunkIndex: chunkMetadata?.chunkIndex,
      totalChunks: chunkMetadata?.totalChunks,
      isLastChunk: chunkMetadata?.isLastChunk,
      chunkId: chunkMetadata?.chunkId,
      participants: draft.participants,
      recordingWithoutParticipants: draft.recordingWithoutParticipants,
      consentDescription: draft.consentDescription,
      consentConfirmed: draft.consentConfirmed,
    }),
  }, 180_000)

  if (!response.ok) {
    const error: ApiError = await readApiError(response)
    throwIfSubmissionRejected(response, error)
    throwIfAuthRejected(response, 'upload drafts')
    throw new Error(error.message || error.error || 'Failed to submit draft recording')
  }

  return response.json()
}

export async function getPresignedUploadUrl(draft: DraftRecording): Promise<PresignUploadResponse> {
  const taxonomySelection = normalizeTaxonomySelection(draft)
  const headers = await buildProtectedAuthHeaders('presign upload', {
    'Content-Type': 'application/json',
  })

  const response = await fetchWithTimeout(getUploadsEndpoint('/presign'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      submissionId: draft.id,
      assetId: taxonomySelection.assetId,
      indicationId: taxonomySelection.indicationId,
      kiqIds: taxonomySelection.kiqIds,
      createdAt: toUtcIsoString(draft.createdAt),
      audioMimeType: draft.blob.type || 'audio/webm',
      durationMs: draft.durationMs,
      participants: draft.participants,
      recordingWithoutParticipants: draft.recordingWithoutParticipants,
      consentDescription: draft.consentDescription,
      consentConfirmed: draft.consentConfirmed,
      chunkSessionId: draft.chunkSessionId,
      totalChunks: draft.chunkCount,
    }),
  }, 45_000)

  if (!response.ok) {
    throwIfAuthRejected(response, 'presign upload')
    const error: ApiError = await readApiError(response)
    throw new Error(error.message || error.error || 'Failed to get presigned upload URL')
  }

  return response.json()
}

export async function finalizeUpload(
  draft: DraftRecording,
  objectKey: string,
): Promise<FinalizeUploadResponse> {
  const taxonomySelection = normalizeTaxonomySelection(draft)
  const headers = await buildProtectedAuthHeaders('finalize upload', {
    'Content-Type': 'application/json',
    'Idempotency-Key': draft.id,
  })

  const response = await fetchWithTimeout(getUploadsEndpoint('/finalize'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      submissionId: draft.id,
      objectKey,
      assetId: taxonomySelection.assetId,
      indicationId: taxonomySelection.indicationId,
      kiqIds: taxonomySelection.kiqIds,
      insightTags: draft.insightTags ?? [],
      createdAt: toUtcIsoString(draft.createdAt),
      audioMimeType: draft.blob.type || 'audio/webm',
      durationMs: draft.durationMs,
      participants: draft.participants,
      recordingWithoutParticipants: draft.recordingWithoutParticipants,
      consentDescription: draft.consentDescription,
      consentConfirmed: draft.consentConfirmed,
      chunkSessionId: draft.chunkSessionId,
      totalChunks: draft.chunkCount,
    }),
  }, 60_000)

  if (!response.ok) {
    const error: ApiError = await readApiError(response)
    throwIfSubmissionRejected(response, error)
    throwIfAuthRejected(response, 'finalize upload')
    throw new Error(error.message || error.error || 'Failed to finalize upload')
  }

  return response.json()
}
